import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ToastController, NavController } from "@ionic/angular";
import { UtilService } from "../../service/util.service";
import { HomeService } from "../home/home.service";

import * as XLSX from "xlsx";

@Component({
  selector: "app-national-stock-all",
  templateUrl: "./national-stock-all.page.html",
  styleUrls: ["./national-stock-all.page.scss"],
})
export class NationalStockAllPage implements OnInit {
  formNationalStockReport: FormGroup;
  counters: string[] = [];
  stockData: any[] = [];

  // Pagination
  currentPage = 1;
  perPage = 50;
  totalPage = 1;

  fileName = "Laporan_Stok_Nasional.xlsx";
  warehouseTypes: any[] = [
    { id: 1, name: "Gudang Pusat" },
    { id: 2, name: "Gudang Cabang" },
  ];

  listCabang: any[] = [];

  constructor(
    private fb: FormBuilder,
    private homeService: HomeService,
    private toastCtrl: ToastController,
    private utilService: UtilService,
    private navCtrl: NavController
  ) {}

  ngOnInit() {
    this.formNationalStockReport = this.fb.group({
      warehouseType: [null],
      cabangId: [""],
      keywordSearch: [null],
    });

    this.loadCabangData();
  }

  async loadCabangData() {
    this.homeService.getCabangList().subscribe(
      async (res: any) => {
        if (res && res.status.code === 200 && res.results.cabang_data) {
          this.listCabang = res.results.cabang_data;
        } else {
          this.toastCtrl
            .create({ duration: 2000, message: "Gagal memuat daftar cabang" })
            .then((t) => t.present());
        }
      },
      async () => {
        this.toastCtrl
          .create({ duration: 2000, message: "Gagal terhubung ke server" })
          .then((t) => t.present());
      }
    );
  }

  async generateReport(page: number = 1) {
    const { warehouseType, cabangId, keywordSearch } =
      this.formNationalStockReport.value;

    if (!warehouseType) {
      const toast = await this.toastCtrl.create({
        message: "Pilih Warehouse Type dulu",
        duration: 2000,
        color: "warning",
      });
      toast.present();
      return;
    }

    await this.utilService.loadingPresent("Memuat data...");

    this.homeService
      .nationalStockAll({
        warehouse_type: warehouseType,
        cabang_id: cabangId,
        keyword_search: keywordSearch,
        page: page,
        per_page: this.perPage,
      })
      .subscribe(
        (res: any) => {
          this.utilService.loadingDismiss();
          if (res.status.code === 200) {
            this.counters = res.results.counters;
            this.stockData = res.results.stock_data;
            this.currentPage = res.results.pagination.current_page;
            this.totalPage = res.results.pagination.last_page;
          } else {
          }
        },
        async () => {
          await this.utilService.loadingDismiss();
          const toast = await this.toastCtrl.create({
            message: "Gagal terhubung ke server",
            duration: 2000,
            color: "danger",
          });
          toast.present();
        }
      );
  }

  async exportToExcel() {
    const { warehouseType, cabangId, keywordSearch } =
      this.formNationalStockReport.value;

    if (!warehouseType) {
      const toast = await this.toastCtrl.create({
        message: "Pilih Warehouse Type dulu",
        duration: 2000,
        color: "warning",
      });
      toast.present();
      return;
    }

    await this.utilService.loadingPresent("Menyiapkan file Excel...");

    this.homeService
      .nationalStockAll({
        warehouse_type: warehouseType,
        cabang_id: cabangId,
        keyword_search: keywordSearch,
        export: true, // ambil semua data full
      })
      .subscribe(
        async (res: any) => {
          await this.utilService.loadingDismiss();

          if (res.status.code === 200 && res.results.stock_data.length > 0) {
            const counters = res.results.counters;
            const stockData = res.results.stock_data;

            // --- PERSIAPAN KETERANGAN LAPORAN ---
            const today = new Date().toLocaleDateString("id-ID"); // Format tanggal Indonesia
            const warehouseName = warehouseType === "1" ? "REGULER" : "PROMO";
            const TOTAL_COLUMNS = 2 + counters.length * 6 + 2; // Pcode + Product Name + (Counter * 6) + Total Global (2)

            // Baris Keterangan (akan di-merge)
            const titleRow: any[] = ["LAPORAN STOK NASIONAL"];
            while (titleRow.length < TOTAL_COLUMNS) titleRow.push("");

            const typeRow: any[] = ["Warehouse Type:", warehouseName];
            while (typeRow.length < TOTAL_COLUMNS) typeRow.push("");

            const dateRow: any[] = ["Tanggal Report:", today];
            while (dateRow.length < TOTAL_COLUMNS) dateRow.push("");

            const emptyRow: any[] = [];
            while (emptyRow.length < TOTAL_COLUMNS) emptyRow.push("");
            // ------------------------------------

            const COLUMNS_PER_COUNTER = 6;

            // Header pertama (colspan per counter)
            const headerRow1: any[] = ["Pcode", "Product Name"];
            for (let i = 0; i < counters.length; i++) {
              headerRow1.push(counters[i], "", "", "", "", "");
            }
            headerRow1.push("Total Existing", "Total End + BDP");

            // Header kedua (sub kolom)
            const headerRow2: any[] = ["", ""];
            for (let i = 0; i < counters.length; i++) {
              headerRow2.push("Early", "In", "Out", "Existing", "BDP", "Total");
            }
            headerRow2.push("", "");

            // Data rows
            const dataRows: any[] = [];
            for (let i = 0; i < stockData.length; i++) {
              const row = stockData[i];
              const r: any[] = [row.pcode, row.product_name];

              for (let j = 0; j < counters.length; j++) {
                const counter = counters[j];
                const c = row[counter] || {};
                const early = c.stock_early || 0;
                const inc = c.stock_in || 0;
                const out = c.stock_out || 0;
                const end = c.stock_end || 0; // Existing
                const bdp = c.bdp || 0; // BDP
                const total = c.stock_total || 0;

                r.push(early, inc, out, end, bdp, total);
              }

              r.push(row.total_end || 0); // Total Existing (stock_end)
              r.push(row.total_end_bdp || 0); // Total End + BDP
              dataRows.push(r);
            }

            // --- MERAKIT DATA KE WORKSHEET ---
            const wsData: any[] = [];

            // 1. Keterangan Laporan (3 baris)
            wsData.push(titleRow);
            wsData.push(typeRow);
            wsData.push(dateRow);
            wsData.push(emptyRow); // Baris kosong sebagai pemisah

            // 2. Header Tabel (2 baris)
            wsData.push(headerRow1); // Baris 5 (Index 4)
            wsData.push(headerRow2); // Baris 6 (Index 5)

            // 3. Data Tabel
            Array.prototype.push.apply(wsData, dataRows);
            // ------------------------------------

            // Buat worksheet
            const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(wsData);

            // Baris data header lama sekarang dimulai dari index 4 dan 5.
            const HEADER_START_ROW_INDEX = 4;
            const merges: XLSX.Range[] = [];

            // Merging Title (dari kolom 0 hingga kolom terakhir)
            merges.push({
              s: { r: 0, c: 0 },
              e: { r: 0, c: TOTAL_COLUMNS - 1 },
            });

            // Merging header tabel (Merges lama, disesuaikan row index-nya)

            // Merging Pcode, Product Name (Baris 5 dan 6)
            merges.push({
              s: { r: HEADER_START_ROW_INDEX, c: 0 },
              e: { r: HEADER_START_ROW_INDEX + 1, c: 0 },
            }); // Pcode
            merges.push({
              s: { r: HEADER_START_ROW_INDEX, c: 1 },
              e: { r: HEADER_START_ROW_INDEX + 1, c: 1 },
            }); // Product Name

            // Merging Total Existing (Baris 5 dan 6)
            const totalExistingCol = 2 + counters.length * COLUMNS_PER_COUNTER;
            merges.push({
              s: { r: HEADER_START_ROW_INDEX, c: totalExistingCol },
              e: { r: HEADER_START_ROW_INDEX + 1, c: totalExistingCol },
            });

            // Merging Total End + BDP (Baris 5 dan 6)
            const totalBdpCol = totalExistingCol + 1;
            merges.push({
              s: { r: HEADER_START_ROW_INDEX, c: totalBdpCol },
              e: { r: HEADER_START_ROW_INDEX + 1, c: totalBdpCol },
            });

            // Merging per counter (Baris 5)
            for (let i = 0; i < counters.length; i++) {
              const startCol = 2 + i * COLUMNS_PER_COUNTER;
              const endCol = startCol + (COLUMNS_PER_COUNTER - 1);
              merges.push({
                s: { r: HEADER_START_ROW_INDEX, c: startCol },
                e: { r: HEADER_START_ROW_INDEX, c: endCol },
              });
            }

            ws["!merges"] = merges;

            // Atur lebar kolom (tidak berubah, hanya perlu dihitung ulang)
            const colWidths: any[] = [{ wch: 10 }, { wch: 30 }]; // Pcode, Product Name

            for (let i = 0; i < counters.length * COLUMNS_PER_COUNTER; i++) {
              colWidths.push({ wch: 10 }); // Sub-kolom per counter (6 kolom)
            }

            // Tambahkan 2 lebar kolom untuk 2 kolom total global
            colWidths.push({ wch: 15 }); // Total Existing
            colWidths.push({ wch: 15 }); // Total End + BDP

            ws["!cols"] = colWidths;

            // Buat workbook dan simpan
            const wb: XLSX.WorkBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
            XLSX.writeFile(
              wb,
              `Laporan_Stok_Nasional_${warehouseName}_${today.replace(
                /\//g,
                "-"
              )}.xlsx`
            );

            const toast = await this.toastCtrl.create({
              message: "File Excel berhasil dibuat",
              duration: 2000,
              color: "success",
            });
            toast.present();
          } else {
            const toast = await this.toastCtrl.create({
              message: "Tidak ada data untuk diexport",
              duration: 2000,
              color: "warning",
            });
            toast.present();
          }
        },
        async (error) => {
          await this.utilService.loadingDismiss();
          console.error("Export error:", error);
          const toast = await this.toastCtrl.create({
            message: "Gagal mengambil data untuk export",
            duration: 2000,
            color: "danger",
          });
          toast.present();
        }
      );
  }

  nextPage() {
    if (this.currentPage < this.totalPage) {
      this.generateReport(this.currentPage + 1);
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.generateReport(this.currentPage - 1);
    }
  }

  clearFilters() {
    this.formNationalStockReport.patchValue({ keywordSearch: null });
  }
}
